<script setup>
import { computed, ref, watch } from 'vue';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

import {
  providerFormContent,
  getDefaultProviderModel,
} from '../../helpers/kraftchatAIProviderConfig';

const props = defineProps({
  availableProviderOptions: {
    type: Array,
    default: () => [],
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['createProvider']);

const form = ref({
  provider: '',
  default_model: '',
  api_key: '',
});

const submitLabel = computed(() => {
  return props.isSaving
    ? providerFormContent.savingLabel
    : providerFormContent.connectLabel;
});

const selectedProviderLabel = computed(() => {
  return (
    props.availableProviderOptions.find(
      option => option.value === form.value.provider
    )?.label || providerFormContent.providerLabel
  );
});

const syncSelectedProvider = () => {
  const hasSelectedProvider = props.availableProviderOptions.some(
    option => option.value === form.value.provider
  );

  if (!hasSelectedProvider) {
    form.value.provider = props.availableProviderOptions[0]?.value || '';
  }

  form.value.default_model = getDefaultProviderModel(form.value.provider);
  form.value.api_key = '';
};

const handleSubmit = () => {
  emit('createProvider', {
    provider: form.value.provider,
    default_model: form.value.default_model,
    api_key: form.value.api_key,
  });
};

watch(
  () => form.value.provider,
  () => {
    form.value.default_model = getDefaultProviderModel(form.value.provider);
    form.value.api_key = '';
  }
);

watch(
  () => props.availableProviderOptions,
  () => {
    syncSelectedProvider();
  },
  { immediate: true }
);
</script>

<template>
  <div class="space-y-4 rounded-xl border border-n-weak bg-n-background p-4">
    <div class="space-y-1">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ providerFormContent.addTitle }}
      </h3>

      <p class="text-sm leading-6 text-n-slate-11">
        {{ providerFormContent.addDescription }}
      </p>
    </div>

    <div class="grid gap-4 md:grid-cols-2">
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ providerFormContent.providerLabel }}
        </label>

        <SelectMenu
          v-model="form.provider"
          class="!w-full [&>button]:!h-10 [&>button]:!w-full [&>button]:!max-w-none [&>button]:!justify-between [&>button]:!px-3 [&>div]:!w-full [&>div]:!max-w-none"
          :label="selectedProviderLabel"
          :options="availableProviderOptions"
          sub-menu-position="bottom"
        />
      </div>

      <Input
        v-model="form.default_model"
        :label="providerFormContent.modelLabel"
        :placeholder="providerFormContent.modelPlaceholder"
        size="md"
        type="text"
      />
    </div>

    <Input
      v-model="form.api_key"
      autocomplete="off"
      :label="providerFormContent.apiKeyLabel"
      :message="providerFormContent.apiKeyHelp"
      :placeholder="providerFormContent.apiKeyPlaceholder"
      size="md"
      type="password"
    />

    <NextButton
      type="button"
      :is-loading="isSaving"
      :label="submitLabel"
      @click="handleSubmit"
    />
  </div>
</template>
